import { encodeToMP3 } from 'dashboard/components/widgets/WootWriter/utils/mp3ConversionUtils';

const MIME_EXTENSION_MAP = {
  'audio/mpeg': 'mp3',
  'audio/mp3': 'mp3',
  'audio/ogg': 'ogg',
  'audio/wav': 'wav',
  'audio/webm': 'webm',
  'audio/x-wav': 'wav',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
};

const floatToInt16Array = floatArray => {
  const int16Array = new Int16Array(floatArray.length);

  for (let index = 0; index < floatArray.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, floatArray[index]));
    int16Array[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }

  return int16Array;
};

const mixAudioBufferToMono = audioBuffer => {
  const channelCount = audioBuffer.numberOfChannels || 1;
  const mixedChannel = new Float32Array(audioBuffer.length);

  for (let channelIndex = 0; channelIndex < channelCount; channelIndex += 1) {
    const channelData = audioBuffer.getChannelData(channelIndex);
    for (let sampleIndex = 0; sampleIndex < audioBuffer.length; sampleIndex += 1) {
      mixedChannel[sampleIndex] += channelData[sampleIndex] / channelCount;
    }
  }

  return floatToInt16Array(mixedChannel);
};

const sanitizeFilenamePart = (value, fallback = 'gravacao') => {
  const normalizedValue = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^0-9A-Za-z]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

  return normalizedValue || fallback;
};

const recordingTimestamp = cdr => {
  const sourceDate = cdr?.ItemDate || cdr?.CallAnswer || cdr?.CallEnd;
  if (!sourceDate) {
    return 'sem-data';
  }

  const timestamp = new Date(sourceDate);
  if (Number.isNaN(timestamp.getTime())) {
    return 'sem-data';
  }

  return timestamp.toISOString().replace(/[:]/g, '-').replace(/\..+$/, '');
};

const recordingExtension = (blob, cdr) => {
  const mimeType = blob?.type?.split(';')[0] || '';
  if (MIME_EXTENSION_MAP[mimeType]) {
    return MIME_EXTENSION_MAP[mimeType];
  }

  return cdr?.WithVideo ? 'webm' : 'mp3';
};

const buildRecordingFilename = (cdr, recordingUid, blob) => {
  const recordingType = cdr?.WithVideo ? 'video' : 'audio';
  const destination = sanitizeFilenamePart(cdr?.Dst || cdr?.Src, 'destino');
  const timestamp = recordingTimestamp(cdr);
  const extension = recordingExtension(blob, cdr);

  return `${recordingType}-call-recording-${destination}-${timestamp}-${recordingUid}.${extension}`;
};

const canConvertRecordingToMp3 = (cdr, blob) => {
  const mimeType = blob?.type?.split(';')[0] || '';

  return (
    !cdr?.WithVideo &&
    !!mimeType &&
    mimeType !== 'audio/mpeg' &&
    mimeType !== 'audio/mp3' &&
    !mimeType.startsWith('video/')
  );
};

const convertRecordingBlobToMp3 = async blob => {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) {
    throw new Error('AudioContext unavailable');
  }

  const audioContext = new AudioContextClass();

  try {
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0));
    const monoSamples = mixAudioBufferToMono(audioBuffer);

    return encodeToMP3(1, audioBuffer.sampleRate, monoSamples);
  } finally {
    if (typeof audioContext.close === 'function') {
      await audioContext.close().catch(() => {});
    }
  }
};

export const prepareBrowserPhoneRecordingUpload = async (cdr, recordingUid, blob) => {
  let uploadBlob = blob;

  if (canConvertRecordingToMp3(cdr, blob)) {
    try {
      uploadBlob = await convertRecordingBlobToMp3(blob);
    } catch {
      uploadBlob = blob;
    }
  }

  return {
    blob: uploadBlob,
    filename: buildRecordingFilename(cdr, recordingUid, uploadBlob),
  };
};
